import {
  EventDetail,
  EventServiceDetail,
  getServiceSegments,
} from "@/types/event";

/**
 * Who a note on an appointment is for. A visit with several services (an IV
 * drip, a HydraFacial and injectables) holds one note per service, each on
 * its own service's template.
 */
export interface EventNoteTarget {
  /** The checklist row's service, when the row belongs to one service. */
  serviceId?: string | null;
  /** The signed-in user. On a row shared by every service, their own service wins. */
  providerId?: string | null;
}

const segmentProviderId = (segment: EventServiceDetail) =>
  segment.providerId ?? segment.provider?.id ?? null;

/** Service segments earliest first; the API returns them in no set order. */
const sortedServiceSegments = (event: EventDetail) =>
  getServiceSegments(event.segments ?? []).toSorted(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );

/**
 * The service segments a note for `target` covers, earliest first: the row's
 * service, else the user's own services on the visit. Empty when the row is
 * shared and the user performs no service on the visit.
 */
function getTargetSegments(
  event: EventDetail,
  { serviceId, providerId }: EventNoteTarget,
): EventServiceDetail[] {
  const segments = sortedServiceSegments(event);
  if (serviceId) return segments.filter((s) => s.service?.id === serviceId);
  if (!providerId) return [];
  return segments.filter((s) => segmentProviderId(s) === providerId);
}

/**
 * Note templates a new note on the appointment could use, earliest service
 * first and without repeats: the row's service's template, else the user's
 * own services' templates, else every service's. Never another provider's
 * service while the user has one on the visit, so an esthetician is not
 * offered the IV nurse's template.
 */
export function getEventNoteTemplateIds(
  event: EventDetail | null | undefined,
  target: EventNoteTarget = {},
): string[] {
  if (!event) return [];
  const segments = getTargetSegments(event, target);
  const candidates =
    target.serviceId || segments.length
      ? segments
      : sortedServiceSegments(event);
  const templateIds = candidates
    .map((s) => s.service?.noteTemplateId)
    .filter((id): id is string => !!id);
  return templateIds.filter((id, i) => templateIds.indexOf(id) === i);
}

/**
 * Note template to pre-select for a new note on the appointment: the one
 * template `getEventNoteTemplateIds` offers. `null` when it offers several,
 * so the provider picks between them rather than getting an arbitrary one.
 */
export function getEventNoteTemplateId(
  event: EventDetail | null | undefined,
  target: EventNoteTarget = {},
): string | null {
  const templateIds = getEventNoteTemplateIds(event, target);
  return templateIds.length === 1 ? templateIds[0] : null;
}

/**
 * The existing note on the appointment that belongs to `target`: the newest
 * note by the target's provider, preferring one on the target's template.
 * A note by the provider of another service on the visit never counts, even
 * on a shared template, so the checklist offers a new note rather than
 * opening someone else's. A note by anyone who performs no service on the
 * visit (staff pre-charting ahead of the appointment) counts, as does one
 * whose author is unknown when it is on the target's template.
 * `undefined` when the target has no note yet. With no target (a shared row
 * and a user who performs nothing on the visit), the newest note.
 */
export function getEventNoteId(
  event: EventDetail | null | undefined,
  target: EventNoteTarget = {},
): string | undefined {
  if (!event) return undefined;
  const noteIds = event.noteIds ?? [];
  // A payload without refs (the calendar list) can't tell notes apart.
  if (!event.noteRefs) return noteIds[0];
  const segments = getTargetSegments(event, target);
  if (!segments.length) return target.serviceId ? undefined : noteIds[0];

  const templateIds = new Set(
    segments.map((s) => s.service?.noteTemplateId).filter(Boolean),
  );
  const providerIds = new Set(segments.map(segmentProviderId).filter(Boolean));
  const otherProviderIds = new Set(
    sortedServiceSegments(event)
      .map(segmentProviderId)
      .filter((id) => id && !providerIds.has(id)),
  );
  const refsById = new Map(event.noteRefs.map((ref) => [ref.id, ref]));
  const notes = noteIds.map((id) => refsById.get(id) ?? { id });
  const onTemplate = (note: (typeof notes)[number]) =>
    !!note.templateId && templateIds.has(note.templateId);
  const targetNotes = notes.filter((note) =>
    note.providerId
      ? providerIds.has(note.providerId) ||
        !otherProviderIds.has(note.providerId)
      : onTemplate(note),
  );
  return (targetNotes.find(onTemplate) ?? targetNotes[0])?.id;
}
